// src/redux/services/inventoryApi.ts
import { createApi, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { RootState } from '../store';
import {
  InventoryRecordsService,
  InventoryRecordItemsService,
  LocationsService,
  CreateInventoryRecordDto,
  UpdateInventoryRecordDto,
  CreateInventoryRecordWithItemsDto,
} from '../../api';
import { parseLocalDate } from '../../utils/formatters/date';
import { resolveApiBaseUrl } from '../../utils/resolveApiBaseUrl';

// YYYY-MM-DD filters represent local calendar days: start at 00:00:00.000,
// end at 23:59:59.999 (inclusive). Mirrors the backend date-range semantics.
const localDayStart = (dateStr?: string): Date | null => {
  if (dateStr == null || dateStr === '') return null;
  const parsed = parseLocalDate(dateStr);
  return isNaN(parsed.getTime()) ? null : parsed;
};

const localDayEnd = (dateStr?: string): Date | null => {
  const start = localDayStart(dateStr);
  if (start == null) return null;
  const end = new Date(start);
  end.setHours(23, 59, 59, 999);
  return end;
};

// Define the base query with auth headers
const baseQuery = fetchBaseQuery({
  baseUrl: resolveApiBaseUrl(),
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Define the API service
export const inventoryApi = createApi({
  reducerPath: 'inventoryApi',
  baseQuery,
  tagTypes: ['Inventory', 'Branches', 'Products'],
  endpoints: (builder) => ({
    // Inventory records endpoints
    getInventoryRecords: builder.query({
      queryFn: async (params: {
        branch_id?: number;
        status?: string;
        limit?: number;
        offset?: number;
        productCode?: string;
        productName?: string;
        startDate?: string;
        endDate?: string;
      }) => {
        try {
          // Using builder.query with queryFn to wrap the generated API
          // This is needed because the direct usage of hooks requires React components

          // Prepare query parameters for the API call
          const queryParams: Record<string, any> = {};

          // Add filters to query params if they exist
          if (params.branch_id) queryParams.locationId = params.branch_id;
          if (params.status) queryParams.status = params.status.toUpperCase();
          if (params.limit) queryParams.limit = params.limit;
          if (params.offset) queryParams.offset = params.offset;
          if (params.productCode) queryParams.productCode = params.productCode;
          if (params.productName) queryParams.productName = params.productName;
          if (params.startDate) queryParams.startDate = params.startDate;
          if (params.endDate) queryParams.endDate = params.endDate;

          const response = await InventoryRecordsService.inventoryRecordsControllerFindAll();

          // Properly handle the response format according to InventoryRecordListResponseDto
          let filteredRecords = response && response.data ? response.data : [];

          // Apply local filtering if needed
          if (params.productCode || params.productName || params.startDate || params.endDate) {
            filteredRecords = filteredRecords.filter((record) => {
              // For product code/name, we need to fetch the items separately as they're not in the response
              // Since we don't have items in the standard response, we can only filter by other properties
              // If product filtering is needed, consider using a different endpoint or modifying the backend

              // We'll skip product filtering here since items aren't available
              const productMatch = !params.productCode && !params.productName;

              // Check date range (registration completion when available)
              const st = String(record.status || '').toUpperCase();
              const regIso =
                st === 'COMPLETED' ? (record.completedAt ?? record.createdAt) : record.createdAt;
              const recordDate = regIso != null ? new Date(regIso) : null;
              const rangeStart = localDayStart(params.startDate);
              const rangeEnd = localDayEnd(params.endDate);
              const startDateMatch =
                rangeStart == null || recordDate == null || rangeStart <= recordDate;
              const endDateMatch = rangeEnd == null || recordDate == null || recordDate <= rangeEnd;

              return productMatch && startDateMatch && endDateMatch;
            });
          }

          // Transform the response to fit our application needs
          const data = {
            data: filteredRecords,
            meta: {
              totalItems: filteredRecords.length,
              itemsPerPage: params.limit || filteredRecords.length,
              totalPages: params.limit ? Math.ceil(filteredRecords.length / params.limit) : 1,
              currentPage:
                params.offset && params.limit ? Math.floor(params.offset / params.limit) + 1 : 1,
            },
          };

          return { data };
        } catch (error) {
          console.error('Error fetching inventory records:', error);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: ['Inventory'],
    }),

    createInventoryRecord: builder.mutation({
      queryFn: async (record: {
        destinationLocationId: number;
        status: string;
        items: { productId: number; quantity: number }[];
      }) => {
        try {
          const recordData = {
            destinationLocationId: record.destinationLocationId,
            status: record.status,
          };

          const response = await InventoryRecordsService.inventoryRecordsControllerCreate(
            recordData as unknown as CreateInventoryRecordDto,
          );

          // Now create each inventory record item with the newly created record's ID
          if (response && response.id && record.items && record.items.length > 0) {
            // Track processed items to prevent duplicates
            const processedItems = new Set();

            // Process items one by one to avoid duplicates
            const itemPromises = record.items.map((item) => {
              // Create a unique key for this item
              const itemKey = `${item.productId}-${item.quantity}`;

              // Skip if this item has already been processed
              if (processedItems.has(itemKey)) {
                return Promise.resolve(); // Return resolved promise for the skipped item
              }

              // Mark as processed
              processedItems.add(itemKey);

              // Create the item
              return InventoryRecordItemsService.inventoryRecordItemsControllerCreate({
                recordId: response.id,
                productId: item.productId,
                quantity: item.quantity,
              });
            });

            // Wait for all item creations to complete
            await Promise.all(itemPromises);
          }

          return { data: response };
        } catch (error) {
          console.error('Error creating inventory record:', error);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: ['Inventory'],
    }),

    // New mutation for creating inventory records with individual units
    createInventoryRecordWithItems: builder.mutation({
      queryFn: async (record: CreateInventoryRecordWithItemsDto) => {
        try {
          const response =
            await InventoryRecordsService.inventoryRecordsControllerCreateWithItems(record);
          return { data: response };
        } catch (error) {
          console.error('Error creating inventory record with items:', error);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: ['Inventory'],
    }),

    updateInventoryRecord: builder.mutation({
      queryFn: async ({ id, data }: { id: string; data: UpdateInventoryRecordDto }) => {
        try {
          const response = await InventoryRecordsService.inventoryRecordsControllerUpdate(id, data);
          return { data: response };
        } catch (error) {
          console.error(`Error updating inventory record ${id}:`, error);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Inventory', id }],
    }),

    getBranches: builder.query({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      queryFn: async (includeInactive: boolean = false) => {
        try {
          const response = await LocationsService.locationsControllerFindAll();
          return { data: response };
        } catch (error) {
          console.error('Error fetching branches:', error);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            } as FetchBaseQueryError,
          };
        }
      },
      providesTags: ['Branches'],
    }),

    // Use this for status updates, similar to what was previously "adjustInventory"
    adjustInventory: builder.mutation({
      queryFn: async ({ id, status }: { id: string; status: string }) => {
        try {
          // Create a data object with just the status field
          const updateData: UpdateInventoryRecordDto = {
            status: status as any, // Using type assertion to bypass type checking for now
          };
          const response = await InventoryRecordsService.inventoryRecordsControllerUpdate(
            id,
            updateData,
          );
          return { data: response };
        } catch (error) {
          console.error(`Error adjusting inventory record ${id}:`, error);
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: String(error),
            } as FetchBaseQueryError,
          };
        }
      },
      invalidatesTags: (result, error, { id }) => [{ type: 'Inventory', id }],
    }),
  }),
});

// Export hooks for usage in components
export const {
  useGetInventoryRecordsQuery,
  useCreateInventoryRecordMutation,
  useUpdateInventoryRecordMutation,
  useGetBranchesQuery,
  useAdjustInventoryMutation,
  useCreateInventoryRecordWithItemsMutation,
} = inventoryApi;
